/**
 * Invoice Item Component
 * Displays a single invoice with status and download
 */

import { Download, FileText } from 'lucide-react';
import React from 'react';

interface InvoiceItemProps {
  date: string;
  amount: number;
  status: string;
  pdfUrl: string | null;
  currency: string;
}

export const InvoiceItem: React.FC<InvoiceItemProps> = ({
  date,
  amount,
  status,
  pdfUrl,
  currency,
}) => {
  const statusColors: Record<string, string> = {
    paid: 'bg-emerald-100 text-emerald-700',
    open: 'bg-amber-100 text-amber-700',
    draft: 'bg-gray-100 text-gray-700',
    void: 'bg-red-100 text-red-700',
  };

  const formatAmount = () => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <FileText size={18} className="text-gray-400" />
        <div>
          <span className="text-sm text-gray-600">{formatDate(date)}</span>
          <span className="ml-3 text-sm font-medium text-gray-900">
            {formatAmount()}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}
        >
          {status}
        </span>
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Download PDF"
          >
            <Download size={16} />
          </a>
        )}
      </div>
    </div>
  );
};
